import { motion } from "framer-motion";
import { useTheme } from "../contexts/theme-provider";
import { cn } from "../lib/utils";
import { CheckCircle2, XCircle, Clock } from "lucide-react";
import { useEffect, useState } from "react";

interface BulkProcessingOverlayProps {
  total: number;
  completed: number;
  failed: number;
  isComplete?: boolean;
  onClose?: () => void;
}

export function BulkProcessingOverlay({
  total,
  completed,
  failed,
  isComplete = false,
  onClose,
}: BulkProcessingOverlayProps) {
  const { theme } = useTheme();
  const remaining = total - completed - failed;
  const successPercent = (completed / total) * 100;
  const failedPercent = (failed / total) * 100;
  // const remainingPercent = (remaining / total) * 100;

  // For the donut chart we use three arcs
  const size = 200;
  const strokeWidth = 20;
  const radius = (size - strokeWidth) / 2;
  const center = size / 2;
  const circumference = 2 * Math.PI * radius;

  // Convert percentages to stroke-dashoffset
  const successOffset = circumference * (1 - successPercent / 100);
  // Failed arc: only span the failed portion and start after the success arc
  const failedDash = circumference * (failedPercent / 100);
  const failedRotation = -90 + (successPercent / 100) * 360;
  // const remainingOffset = circumference; // full circle for remaining (will be grey)

  // Animation for the spinning effect while waiting
  const [spin, setSpin] = useState(0);
  useEffect(() => {
    if (isComplete) return;
    const interval = setInterval(() => {
      setSpin((prev) => (prev + 1) % 360);
    }, 50);
    return () => clearInterval(interval);
  }, [isComplete]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-md"
    >
      <div
        className={`absolute inset-0 ${
          theme === "dark" ? "bg-black/70" : "bg-white/90 dark:bg-slate-900/90"
        }`}
      />
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ type: "spring", damping: 15 }}
        className={cn(
          "relative z-10 w-full max-w-md mx-4 p-8 rounded-3xl shadow-2xl border-2",
          theme === "dark"
            ? "bg-gradient-to-br from-gray-800 to-gray-900 border-gray-700"
            : "bg-gradient-to-br from-white dark:from-slate-900 to-blue-50 dark:to-blue-900/30 border-blue-100 dark:border-blue-800"
        )}
      >
        <h3 className={cn(
          "text-2xl font-bold text-center mb-6",
          theme === "dark" ? "text-white" : "text-gray-900 dark:text-white"
        )}>
          {isComplete ? "Bulk Processing Complete" : "Processing Bulk Files"}
        </h3>

        {/* Donut chart */}
        <div className="relative w-48 h-48 mx-auto mb-6">
          <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
            {/* Background circle (remaining) */}
            <circle
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              stroke={theme === "dark" ? "#374151" : "#e5e7eb"}
              strokeWidth={strokeWidth}
            />
            {/* Success arc */}
            <circle
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              stroke="#10b981"
              strokeWidth={strokeWidth}
              strokeDasharray={circumference}
              strokeDashoffset={successOffset}
              strokeLinecap="round"
              transform={`rotate(-90 ${center} ${center})`}
            />
            {/* Failed arc */}
            {failed > 0 && (
              <circle
                cx={center}
                cy={center}
                r={radius}
                fill="none"
                stroke="#ef4444"
                strokeWidth={strokeWidth}
                strokeDasharray={`${failedDash} ${circumference}`}
                strokeDashoffset={0}
                strokeLinecap="round"
                transform={`rotate(${failedRotation} ${center} ${center})`}
              />
            )}
            {/* Spinning ring when not complete */}
            {!isComplete && (
              <circle
                cx={center}
                cy={center}
                r={radius - 2}
                fill="none"
                stroke="url(#gradient)"
                strokeWidth={4}
                strokeDasharray="8 8"
                strokeLinecap="round"
                style={{ rotate: `${spin}deg`, transformOrigin: `${center}px ${center}px` }}
              />
            )}
            <defs>
              <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#10b981" />
                <stop offset="100%" stopColor="#34d399" />
              </linearGradient>
            </defs>
          </svg>

          {/* Center text */}
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className={cn(
              "text-3xl font-black",
              theme === "dark" ? "text-white" : "text-gray-900 dark:text-white"
            )}>
              {completed + failed}/{total}
            </span>
            <span className={cn(
              "text-xs font-medium",
              theme === "dark" ? "text-gray-400" : "text-gray-500 dark:text-slate-400"
            )}>
              {isComplete ? "Done" : "Processing"}
            </span>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 text-green-500 dark:text-green-400">
              <CheckCircle2 className="w-4 h-4" />
              <span className="font-bold">{completed}</span>
            </div>
            <p className={cn(
              "text-xs",
              theme === "dark" ? "text-gray-400" : "text-gray-500 dark:text-slate-400"
            )}>Completed</p>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 text-red-500 dark:text-red-400">
              <XCircle className="w-4 h-4" />
              <span className="font-bold">{failed}</span>
            </div>
            <p className={cn(
              "text-xs",
              theme === "dark" ? "text-gray-400" : "text-gray-500 dark:text-slate-400"
            )}>Failed</p>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 text-gray-400">
              <Clock className="w-4 h-4" />
              <span className="font-bold">{remaining}</span>
            </div>
            <p className={cn(
              "text-xs",
              theme === "dark" ? "text-gray-400" : "text-gray-500 dark:text-slate-400"
            )}>Remaining</p>
          </div>
        </div>

        {/* Close button when complete */}
        {isComplete && onClose && (
          <button
            onClick={onClose}
            className={cn(
              "w-full py-3 rounded-xl font-bold transition-all",
              theme === "dark"
                ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700"
                : "bg-gradient-to-r from-blue-500 to-purple-500 text-white hover:from-blue-600 hover:to-purple-600"
            )}
          >
            Close
          </button>
        )}
      </motion.div>
    </motion.div>
  );
}